import {
  fetchWithPagination,
  getBaseScheduleBEUrl,
  getRequestWithAllPages,
  splitCompoundId,
} from '../../helpers';
import { COMPOUND_ID_DELIMITER } from '../../helpers/consts';
import {
  Plan,
  PlanTaskReferencePickerPlugin,
  TaskEntity,
  TaskResponse,
} from '../types';

interface TaskCompoundId {
  planId: string;
  taskId: string;
  entityId: string;
}

type PlanTasksMap = Record<string, TaskCompoundId[]>;

/**
 *  Fetches paginated tasks for a given project and task compound ids.
 */
const getTasks = async (
  projectId: string,
  planId: string,
  taskIds: string[],
) => {
  const baseScheduleUrl = getBaseScheduleBEUrl();
  const encodedPlanId = encodeURIComponent(planId);
  const encodedTaskIds = taskIds.map(encodeURIComponent).join(',');

  try {
    return await fetchWithPagination<TaskResponse>(
      `${baseScheduleUrl}/projects/${projectId}/plans/${encodedPlanId}/tasks?filter[taskId]=${encodedTaskIds}`,
    );
  } catch (err) {
    console.warn(err);
    return [] as TaskResponse[];
  }
};

/**
 * Fetches plans for a given project.
 */
const getPlans = (projectId: string) => {
  const baseScheduleUrl = getBaseScheduleBEUrl();

  return getRequestWithAllPages<Plan>(
    `${baseScheduleUrl}/projects/${projectId}/plans`,
  );
};

/**
 * Fetches plans for a given project.
 */
const getParentTasksMap = async (
  projectId: string,
  tasksResponse: TaskResponse[],
): Promise<Record<string, TaskResponse>> => {
  const paginatedParentTasksResponse = await Promise.all(
    tasksResponse
      .filter((tasksResponse) => !!tasksResponse.parentTaskId)
      .map(({ parentTaskId, planId }) =>
        getTasks(projectId, planId, [parentTaskId as string]),
      ),
  );

  const parentTasksResponse = paginatedParentTasksResponse.flat();

  return parentTasksResponse.reduce(
    (accumulatedMap, parentTasksResponse) => ({
      ...accumulatedMap,
      [parentTasksResponse.id]: parentTasksResponse,
    }),
    {} as Record<string, TaskResponse>,
  );
};

/**
 *  Fetches paginated tasks for a given project and task compound ids.
 */
const getPaginatedTasks = async (
  projectId: string,
  taskCompoundIds: TaskCompoundId[],
): Promise<TaskResponse[][]> => {
  const planTasksMap: PlanTasksMap = taskCompoundIds.reduce(
    (accumulatedMap, taskCompoundId) => ({
      ...accumulatedMap,
      [taskCompoundId.planId]: [
        ...(accumulatedMap[taskCompoundId.planId] ?? []),
        taskCompoundId,
      ],
    }),
    {} as PlanTasksMap,
  );

  return Promise.all(
    Object.entries(planTasksMap).map(async ([planId, taskCompoundIds]) => {
      const taskIds = taskCompoundIds
        .map(({ taskId }) => taskId)
        .filter(Boolean);

      return getTasks(projectId, planId, taskIds);
    }),
  );
};

/**
 *  Finds the entity id for a given task response.
 */
const findEntityId = (
  taskCompoundIds: TaskCompoundId[],
  taskResponse: TaskResponse,
) =>
  taskCompoundIds.find(
    (taskCompoundId) =>
      taskCompoundId.taskId === taskResponse.id.toString() &&
      taskCompoundId.planId === taskResponse.planId,
  )?.entityId;

/**
 * Formats the paginated tasks response into the expected format.
 *
 * The tasks are enriched with the plan and parent task information.
 */
const formatPaginatedTasksResponse = async (
  projectId: string,
  taskCompoundIds: TaskCompoundId[],
  paginatedTasksResponse: TaskResponse[][],
): Promise<{ data: TaskEntity[] }> => {
  const tasksResponse = paginatedTasksResponse.flat();

  const [plans, parentTasksMap] = await Promise.all([
    getPlans(projectId),
    getParentTasksMap(projectId, tasksResponse),
  ]);

  const data = tasksResponse
    .map((taskResponse) => {
      const entityId = findEntityId(taskCompoundIds, taskResponse);

      if (!entityId) {
        return undefined;
      }

      let parentTask;
      const plan = plans.find((plan) => plan.id === taskResponse.planId);

      if (taskResponse.parentTaskId) {
        parentTask = parentTasksMap[taskResponse.parentTaskId];
      }

      const task = {
        ...taskResponse,
        plan,
        parentTask,
      };

      return {
        id: entityId,
        task,
      } satisfies TaskEntity;
    })
    .filter(Boolean) as TaskEntity[];

  return { data };
};

const formatTaskCompoundIds = (entityIds: string[]): TaskCompoundId[] =>
  entityIds
    .filter((entityId) => entityId.includes(COMPOUND_ID_DELIMITER))
    .map((entityId: string) => {
      const [planId, taskId] = splitCompoundId(entityId);

      return { planId, taskId, entityId };
    });

/**
 * Fetches tasks by their unique ids.
 */
export const getEntitiesByIds: PlanTaskReferencePickerPlugin['getEntitiesByIds'] =
  async ({ ids: entityIds, projectId }) => {
    const taskCompoundIds = formatTaskCompoundIds(entityIds);
    const paginatedTasksResponse = await getPaginatedTasks(
      projectId,
      taskCompoundIds,
    );

    return formatPaginatedTasksResponse(
      projectId,
      taskCompoundIds,
      paginatedTasksResponse,
    );
  };
